// @ts-strict
import {useCallback} from 'react';
import {ApolloError} from '@apollo/client';
import {type GraphQLFormattedError} from 'graphql';

import {SupportCaseSource, useSubmitSupportCaseMutation} from '@/graphql/types';
import {SadPathPages, trackSadPath} from '@/utils/tracking/sadPath';
import {
  FIELD_IDS,
  type FieldId,
  type SubmitResult,
  type SupportCaseInput,
  type SupportMessageSource,
  type SupportMessageSurface,
} from './types';

type FieldErrorExtras = {type?: string; key?: string};

const SUPPORT_CASE_SOURCES: Record<
  SupportMessageSurface | SupportMessageSource,
  SupportCaseSource
> = {
  contact_us_modal: SupportCaseSource.ContactUsModal,
  contact_us_page: SupportCaseSource.ContactUsPage,
  standalone_route: SupportCaseSource.StandaloneRoute,
  native_ios: SupportCaseSource.NativeIos,
  native_android: SupportCaseSource.NativeAndroid,
};

const isFieldId = (key: string): key is FieldId => (FIELD_IDS as readonly string[]).includes(key);

const toFieldErrors = (
  graphQLErrors: readonly GraphQLFormattedError[],
): Partial<Record<FieldId, string>> | undefined => {
  const fieldErrors: Partial<Record<FieldId, string>> = {};

  graphQLErrors.forEach(error => {
    const {type, key} = error as GraphQLFormattedError & FieldErrorExtras;
    if (type !== 'field' || !key || !isFieldId(key)) return;

    fieldErrors[key] = error.message;
  });

  return Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined;
};

export const useSubmitSupportCase = (): ((input: SupportCaseInput) => Promise<SubmitResult>) => {
  const [submitSupportCase] = useSubmitSupportCaseMutation();

  return useCallback(
    async input => {
      const reportFailure = (reason: string): SubmitResult => {
        trackSadPath(SadPathPages.CONTACT_US, 'support_message_submit_failed', {
          message_type: 'validation_message',
          type: 'submission_failed',
          details: {reason, source: input.source},
        });

        return {success: false, reason};
      };

      try {
        const {data} = await submitSupportCase({
          variables: {
            input: {...input, source: input.source && SUPPORT_CASE_SOURCES[input.source]},
          },
        });
        if (data?.submitSupportCase?.success) return {success: true};

        return reportFailure(data?.submitSupportCase?.message ?? 'unknown');
      } catch (error) {
        if (error instanceof ApolloError && error.graphQLErrors.length > 0) {
          const fieldErrors = toFieldErrors(error.graphQLErrors);
          if (fieldErrors) return {success: false, fieldErrors};

          return reportFailure('salesforce_rejected');
        }

        return reportFailure('network_error');
      }
    },
    [submitSupportCase],
  );
};
